"use client";

import React from "react";
import { useGraphStore } from "@/store/graph-store";

interface Lane {
  id: string;
  label: string;
  y: number;
  height: number;
  bgColor: string;
  borderColor: string;
}

export function Swimlanes() {
  const layoutMode = useGraphStore((state) => state.layoutMode);

  if (layoutMode !== "swimlane") return null;

  // Spacing boundaries corresponding to LANE_HEIGHTS in layout-engine.ts:
  // - Strategy (Y = 80): boundary from Y = -40 to 200
  // - Work/Delivery (Y = 320): boundary from Y = 200 to 440
  // - Finance (Y = 560): boundary from Y = 440 to 680
  // - People (Y = 800): boundary from Y = 680 to 920
  // - Operations/Others (Y = 1040): boundary from Y = 920 to 1160
  const lanes: Lane[] = [
    {
      id: "strategy",
      label: "Strategy (Objectives)",
      y: -40,
      height: 240,
      bgColor: "rgba(91, 92, 226, 0.02)",
      borderColor: "rgba(91, 92, 226, 0.15)",
    },
    {
      id: "work",
      label: "Work (Projects)",
      y: 200,
      height: 240,
      bgColor: "rgba(46, 116, 199, 0.02)",
      borderColor: "rgba(46, 116, 199, 0.15)",
    },
    {
      id: "finance",
      label: "Finance (Ledger)",
      y: 440,
      height: 240,
      bgColor: "rgba(46, 148, 101, 0.02)",
      borderColor: "rgba(46, 148, 101, 0.15)",
    },
    {
      id: "people",
      label: "People (Agents)",
      y: 680,
      height: 240,
      bgColor: "rgba(21, 154, 138, 0.02)",
      borderColor: "rgba(21, 154, 138, 0.15)",
    },
    {
      id: "operations",
      label: "Operations & Others",
      y: 920,
      height: 240,
      bgColor: "rgba(102, 112, 133, 0.02)",
      borderColor: "rgba(102, 112, 133, 0.15)",
    },
  ];

  return (
    <>
      {/* Global CSS to handle the node & edge transitions during layout toggling */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            /* Smoothly animate node translation without lagging during active user dragging */
            .react-flow__node:not(.dragging) {
              transition: transform 700ms cubic-bezier(0.16, 1, 0.3, 1);
            }
            /* Smoothly animate edge paths warping between layouts */
            .react-flow__edge-path {
              transition: d 700ms cubic-bezier(0.16, 1, 0.3, 1);
            }
          `,
        }}
      />

      <div className="absolute inset-0 pointer-events-none select-none z-0">
        <svg className="w-full h-full min-h-[1600px] overflow-visible">
          {lanes.map((lane) => (
            <g key={lane.id} className="animate-fade-in">
              {/* Lane background band stretching horizontally */}
              <rect
                x="-10000"
                y={lane.y}
                width="20000"
                height={lane.height}
                fill={lane.bgColor}
              />
              {/* Top dashed divider boundary line */}
              <line
                x1="-10000"
                y1={lane.y}
                x2="10000"
                y2={lane.y}
                stroke={lane.borderColor}
                strokeWidth="1.5"
                strokeDasharray="6 8"
              />
              {/* Bottom dashed boundary line for the final lane */}
              {lane.id === "operations" && (
                <line
                  x1="-10000"
                  y1={lane.y + lane.height}
                  x2="10000"
                  y2={lane.y + lane.height}
                  stroke={lane.borderColor}
                  strokeWidth="1.5"
                  strokeDasharray="6 8"
                />
              )}
              {/* Text label placed slightly left of the layout start (X = -200) */}
              <text
                x="-200"
                y={lane.y + 40}
                className="fill-text-secondary/70 font-semibold text-xs uppercase tracking-wider select-none"
                style={{
                  fontFamily: "var(--font-sans)",
                }}
              >
                {lane.label}
              </text>
            </g>
          ))}
        </svg>
      </div>
    </>
  );
}
